import { useId } from "react";
import type { KeyboardEvent, RefObject } from "react";
import { X } from "lucide-react";
import { DOCK_LABELS } from "../../shared/constants/pageAgentLabels";

interface AgentComposerProps {
  question: string;
  onChange: (text: string) => void;
  onSend: () => void;
  onStop: () => void;
  /** True while a run is in flight: the box and Send are disabled, Stop is offered. */
  busy: boolean;
  /** The field action pinned to this question, shown above the box; null when none is. */
  chip?: string | null;
  /** Removes the pinned field action. */
  onClearChip?: () => void;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
}

const KEY_ENTER = "Enter";

/** The question box. Enter sends; Shift+Enter inserts a newline. */
export function AgentComposer({ question, onChange, onSend, onStop, busy, chip = null, onClearChip, inputRef }: AgentComposerProps) {
  const id = useId();
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === KEY_ENTER && !event.shiftKey) {
      event.preventDefault();
      onSend();
    }
  };
  return (
    <div className="pa-composer">
      <label className="pa-composer-label" htmlFor={id}>{DOCK_LABELS.question}</label>
      {chip !== null && (
        <span className="pa-composer-chip">{chip}
          <button type="button" className="pa-composer-chip-remove" onClick={onClearChip}>
            <X aria-hidden="true" className="pa-icon" />
            <span className="pa-visually-hidden">{DOCK_LABELS.removeChip(chip)}</span>
          </button>
        </span>
      )}
      <textarea id={id} ref={inputRef} className="pa-composer-input" rows={3} value={question} disabled={busy}
        onChange={(event) => onChange(event.target.value)} onKeyDown={onKeyDown} />
      <div className="pa-composer-actions">
        <p className="pa-composer-hint">{DOCK_LABELS.hint}</p>
        <div className="pa-composer-buttons">
          {busy && <button type="button" className="pa-button pa-button-secondary" onClick={onStop}>{DOCK_LABELS.stop}</button>}
          <button type="button" className="pa-button" onClick={onSend} disabled={busy || question.trim() === ""}>
            {busy ? DOCK_LABELS.sending : DOCK_LABELS.send}
          </button>
        </div>
      </div>
    </div>
  );
}
